import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../services/api';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Gift, Users, Check, Clock, User, Building } from 'lucide-react';

const AdminReferrals = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchReferrals();
  }, [user, navigate, statusFilter, page]);

  const fetchReferrals = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('skip', page * 50);
      params.append('limit', 50);

      const res = await api.get(`/admin/referrals?${params.toString()}`);
      setReferrals(res.data.referrals);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRewarded = async (referralId) => {
    try {
      await api.post(`/admin/referrals/${referralId}/mark-rewarded`);
      toast.success('Referral marked as rewarded');
      fetchReferrals();
    } catch (error) {
      toast.error('Failed to mark referral');
    }
  };

  return (
    <AdminLayout title="Referrals" breadcrumbs={[{ label: 'Referrals' }]}>
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#1B5AFF]/20 flex items-center justify-center">
                <Gift className="w-6 h-6 text-[#1B5AFF]" />
              </div>
              <div>
                <p className="text-[#5B6472] text-sm">Total Referrals</p>
                <p className="text-2xl font-bold text-[#0A0F1E]">{total}</p>
              </div>
            </div>
          </Card>
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <Check className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-[#5B6472] text-sm">Completed</p>
                <p className="text-2xl font-bold text-[#0A0F1E]">
                  {referrals.filter(r => r.status === 'completed').length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-[#5B6472] text-sm">Pending</p>
                <p className="text-2xl font-bold text-[#0A0F1E]">
                  {referrals.filter(r => r.status === 'pending').length}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 mb-6">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px] bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-lg">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
              <SelectItem value="all" className="text-[#0A0F1E]">All Status</SelectItem>
              <SelectItem value="pending" className="text-[#0A0F1E]">Pending</SelectItem>
              <SelectItem value="completed" className="text-[#0A0F1E]">Completed</SelectItem>
            </SelectContent>
          </Select>
        </Card>

        {/* Referrals Table */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F5F8FF]/50">
                <tr>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Referrer</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Type</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Referred User</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Status</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Reward</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Date</th>
                  <th className="text-right p-4 text-[#5B6472] text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8EDF5]">
                {referrals.map((referral) => (
                  <tr key={referral.id} className="hover:bg-[#F5F8FF]/30">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {referral.referrer_type === 'employer' ? (
                          <Building className="w-4 h-4 text-blue-400" />
                        ) : (
                          <User className="w-4 h-4 text-[#1B5AFF]" />
                        )}
                        <span className="text-[#0A0F1E]">{referral.referrer_name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        referral.referrer_type === 'employer' ? 'bg-blue-500/20 text-blue-400' : 'bg-[#1B5AFF]/20 text-[#1B5AFF]'
                      }`}>
                        {referral.referrer_type}
                      </span>
                    </td>
                    <td className="p-4 text-[#0A0F1E]">{referral.referred_name}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        referral.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {referral.status}
                      </span>
                    </td>
                    <td className="p-4">
                      {referral.reward_issued ? (
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs flex items-center gap-1 w-fit">
                          <Check className="w-3 h-3" /> Issued
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs">Pending</span>
                      )}
                    </td>
                    <td className="p-4 text-[#5B6472] text-sm">
                      {referral.created_at ? new Date(referral.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-4">
                      {!referral.reward_issued && referral.status === 'completed' && (
                        <Button 
                          size="sm" 
                          onClick={() => handleMarkRewarded(referral.id)}
                          className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        >
                          Mark Rewarded
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {referrals.length === 0 && !loading && (
            <div className="p-12 text-center">
              <Gift className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
              <p className="text-[#5B6472]">No referrals found</p>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-[#E8EDF5]">
            <p className="text-[#5B6472] text-sm">Total: {total} referrals</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="border-[#E8EDF5] text-[#0A0F1E]">Previous</Button>
              <Button size="sm" variant="outline" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * 50 >= total} className="border-[#E8EDF5] text-[#0A0F1E]">Next</Button>
            </div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminReferrals;
